import { createAction } from "@reduxjs/toolkit";
import {
  SEEK_ANSWER_FOR_QUESTION_REQUEST,
  SEEK_EVENT_FOR_QUESTION_SUCCEEDED,
  SEEK_ANSWER_FOR_QUESTION_SUCCEEDED,
  SEEK_ANSWER_FOR_QUESTION_FAILED,
  INVALIDATE_SEEK_SESSION,
  INVALIDATE_SEEK_CHAT_ANIMATION,
  PROVIDE_FEEDBACK_FOR_CHAT,
  PROVIDING_FEEDBACK_FOR_CHAT_SUCCEEDED,
  PROVIDING_FEEDBACK_FOR_CHAT_FAILED
} from "./actionTypes.js";

import axios from 'axios';
import buildUrl from 'build-url-ts';
import { call, put, takeEvery } from "redux-saga/effects";
import { request } from "http";
import { SeekAnswerErrorResponse, SeekAnswerRequest, SeekAnswerResponse, SeekEventResponse } from "../../types/seek.js";
import { parseSeekChunk } from "../../utils/parseSeekChunk.js";

const apiUrl = `${process.env.API_URL}`;

// generate answer for query 
export const seekAnswerForQuestion = createAction<SeekAnswerRequest>(SEEK_ANSWER_FOR_QUESTION_REQUEST);
const seekAnswerForQuestionSucceeded = createAction<SeekAnswerResponse>(SEEK_ANSWER_FOR_QUESTION_SUCCEEDED);
const seekEventForQuestionSucceeded = createAction<SeekEventResponse>(SEEK_EVENT_FOR_QUESTION_SUCCEEDED);
const seekAnswerForQuestionFailed = createAction<SeekAnswerErrorResponse>(SEEK_ANSWER_FOR_QUESTION_FAILED);

const seekAnswerForQuestionSaga = function* (action: ReturnType<typeof seekAnswerForQuestion>): any {
  const { uuid, session_id, question, dataset_id } = action.payload as SeekAnswerRequest;

  let url = buildUrl(apiUrl, {
    path: '/data-seek/answer'
  });

  const data = {
    dataset_resource_id: dataset_id,
    session_id: session_id,
    question: question,
  }

  console.log('data', data);

  const seekAnswerApi = () => {
    return axios({
      method: 'POST',
      url: url,
      data: data,
      responseType: 'stream',  // Required to get stream of data
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream' // expects to receive a server-sent event stream
      },
      adapter: 'fetch'
    }).then((response: { data: any; }) => response.data);
  };

  try {
    const response = yield call(seekAnswerApi);
    const reader = response.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = '';
    let pendingEventLine: string | null = null;
    while (true) {
      const { done, value } = yield reader.read();
      if (done) break;
      buffer += value;
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('event:')) {
          pendingEventLine = trimmed;
        } else if (trimmed.startsWith('data:') && pendingEventLine) {
          const chunk = `${pendingEventLine}\n${trimmed}`;
          pendingEventLine = null;
          const event = parseSeekChunk(chunk);
          if (event) {
            yield put(seekEventForQuestionSucceeded({
              uuid,
              session_id,
              response: event,
            }));
            if (event.type === 'final') {
              const payload = event as { message?: string; session_id?: string; artifacts?: any; chat_id?: string; dataset_source_ids?: string[] };
              yield put(seekAnswerForQuestionSucceeded({
                uuid,
                response: {
                  id: payload.chat_id || uuid,
                  session_id: payload.session_id || session_id,
                  question,
                  answer: payload.message || '',
                  artifacts: payload.artifacts,
                  dataset_source_ids: payload.dataset_source_ids,
                  created_date: Date.now(),
                  modified_date: Date.now(),
                },
              }));
            }
          }
        }
      }
    }
  } catch (error) {
    yield put(seekAnswerForQuestionFailed({
      uuid: uuid,
      session: session_id,
      error: error
    }));
  }
}

export const invalidateSeekSession = createAction<string>(INVALIDATE_SEEK_SESSION);

//Provide feedback for chat message
export const provideFeedbackForChat = createAction<any>(PROVIDE_FEEDBACK_FOR_CHAT);
export const providingFeedbackForChatSucceeded = createAction<any>(PROVIDING_FEEDBACK_FOR_CHAT_SUCCEEDED);
export const providingFeedbackForChatFailed = createAction<any>(PROVIDING_FEEDBACK_FOR_CHAT_FAILED);

const provideFeedbackForChatSaga = function* (action: ReturnType<typeof provideFeedbackForChat>): any {
  const { chat_id, dataset_resource_id, helpful, comment } = action.payload;

  const url = buildUrl(`${apiUrl}`, {
    path: `/feedback`,
  });

  const data = {
    chat_id: chat_id,
    dataset_resource_id: dataset_resource_id,
    helpful: helpful,
    comment: comment,
  }

  const provideFeedbackForChatAPI = () => {
    return axios({
      method: 'POST',
      url: url,
      data: data
    }).then((response: { data: any; }) => response.data);
  };

  try {
    const response = yield call(provideFeedbackForChatAPI);
    yield put(providingFeedbackForChatSucceeded({
      chat_id: chat_id,
      helpful: helpful,
    }));
  } catch (error) {
    console.log(error);
    yield put(providingFeedbackForChatFailed({
      chat_id: chat_id,
      error: error
    }));
  }
}

export const invalidateSeekChatAnimation = createAction<string>(INVALIDATE_SEEK_CHAT_ANIMATION);

export default function* seekSaga() {
  yield takeEvery(SEEK_ANSWER_FOR_QUESTION_REQUEST, seekAnswerForQuestionSaga);
  yield takeEvery(PROVIDE_FEEDBACK_FOR_CHAT, provideFeedbackForChatSaga);
}